import { PhoneIcon } from '@heroicons/react/solid';
import { gql } from 'graphql-tag';
import { RefObject, createRef, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { ContactsQuery } from '~/generated/graphql';
import { buildRelToLabel } from '~/helpers/contactTypeMappings';
import { Avatar } from '../ui/Avatar';
import { CardNew } from '../ui/CardNew';

type Contact = ContactsQuery['contacts'][number];

type Props = {
  contacts: readonly Contact[];
  selectedId?: string;
};

const CONTACT_TYPE_MAPPINGS_Q = gql`
  query ContactTypeMappingsForListItem {
    contactTypeMappings {
      label
      relationships
    }
  }
`;

const CONTACT_TYPES_PRESET_Q = gql`
  query ContactTypesPreset_ForList($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`;

export function ContactList({ contacts, selectedId }: Props) {
  const { t } = useTranslation();
  const [{ data: mappingsData }] = useQuery({ query: CONTACT_TYPE_MAPPINGS_Q });
  const mappings: { label: string; relationships: string[] }[] =
    mappingsData?.contactTypeMappings ?? [];

  const [{ data: presetData }] = useQuery({
    query: CONTACT_TYPES_PRESET_Q,
    variables: { preset: true },
    requestPolicy: 'cache-and-network',
  });
  const presetSet = useMemo(
    () =>
      new Set<string>(
        (presetData?.contactTypes ?? []).map((v: unknown) => String(v))
      ),
    [presetData?.contactTypes]
  );

  const relToLabel = useMemo(() => buildRelToLabel(mappings), [mappings]);

  const refs = useRef<{ [x: string]: RefObject<HTMLDivElement> }>({});
  // Lazily create refs only for items we render; reuse across renders
  const ensureRef = (id: string) => {
    if (!refs.current[id]) refs.current[id] = createRef();
    return refs.current[id];
  };

  useEffect(() => {
    if (!Object.keys(refs.current).length) return;

    if (selectedId && refs.current[selectedId]?.current) {
      refs.current[selectedId].current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedId]);

  return (
    <div className='max-w-full flex-1 overflow-x-hidden'>
      {contacts.map((item) => (
        <div key={item.id} ref={ensureRef(item.id)}>
          <CardNew
            isSelected={selectedId === item.id.toString()}
            {...getListItemProps(
              item,
              relToLabel,
              presetSet,
              (label: string) => {
                if (label === 'Other') {
                  const key = 'contactRelationships.Other';
                  const translated = t(key);
                  return translated === key ? label : translated;
                }
                if (presetSet.has(label)) {
                  const key = `contactRelationships.${label}`;
                  const translated = t(key);
                  return translated === key ? label : translated;
                }
                return label;
              }
            )}
          />
        </div>
      ))}
    </div>
  );
}

const getListItemProps = (
  contact: Contact,
  relToLabel: Map<string, string>,
  presetSet: Set<string>,
  renderLabel: (label: string) => string
) => ({
  id: contact.id,
  linkTo: `/contacts/${contact.id}`,
  titleText: contact.name,
  secondaryText: (contact.relationship ?? [])
    .map((r) => relToLabel.get(String(r)) ?? String(r))
    // If mapping yields a label that is not a preset, revert to the raw relationship key
    .map((label, idx, arr) => {
      const raw = String(contact.relationship?.[idx] ?? label);
      return presetSet.has(label) ? label : raw;
    })
    .map((label) => renderLabel(label))
    .join(', '),
  tertiaryText: contact.email,
  image: (
    <Avatar image={contact.image} name={contact.name} size='full' square />
  ),
  showImage: true,
  hero: { directive: <PhoneLink number={contact.phone} /> },
  status: contact.status,
});

type PhoneLinkProps = {
  number: string | null | undefined;
};

function PhoneLink({ number }: PhoneLinkProps) {
  if (!number) {
    return null;
  }

  return (
    <button
      className='underline'
      onClick={() => (window.location.href = `tel:${number}`)}
    >
      <div className='flex gap-2'>
        <PhoneIcon className='mt-0.5 h-4 w-4 text-grey-90' />
        {number}
      </div>
    </button>
  );
}
